/**
 * Deal Pipeline Dashboard
 * Internal CRM for DeCarbonIQ team — manage all SME→FI deal flow.
 * Data: /api/deals
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import {
  TrendingUp, Filter, RefreshCw, ChevronDown, X, Check,
  AlertTriangle, DollarSign, Clock, Shield, Users, Sparkles,
  ArrowRight, Plus, FileText, ExternalLink
} from "lucide-react";
import FinancingReadinessScore from "@/components/FinancingReadinessScore";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Constants ─────────────────────────────────────────────────────────────
const STAGES = [
  { key:"incoming_interest", label:"Incoming",     color:"bg-blue-500",    text:"text-blue-700 bg-blue-50 border-blue-200"  },
  { key:"qualified",         label:"Qualified",    color:"bg-indigo-500",  text:"text-indigo-700 bg-indigo-50 border-indigo-200" },
  { key:"fi_engaged",        label:"FI Engaged",   color:"bg-amber-500",   text:"text-amber-700 bg-amber-50 border-amber-200"  },
  { key:"decision",          label:"Decision",     color:"bg-orange-500",  text:"text-orange-700 bg-orange-50 border-orange-200"},
  { key:"closed_funded",     label:"Funded",       color:"bg-emerald-500", text:"text-emerald-700 bg-emerald-50 border-emerald-200"},
  { key:"closed_lost",       label:"Lost",         color:"bg-red-400",     text:"text-red-700 bg-red-50 border-red-200"},
];
const PRIORITY_STYLE = {
  high:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-blue-50 text-blue-700 border-blue-200",
  low:    "bg-slate-100 text-slate-600 border-slate-200",
};
const INTEREST_STYLE = {
  high:      "bg-emerald-50 text-emerald-700",
  medium:    "bg-amber-50 text-amber-700",
  exploring: "bg-slate-100 text-slate-600",
};
const fmtK  = (n) => !n ? "—" : n>=1e6 ? `$${(n/1e6).toFixed(1)}M` : n>=1e3 ? `$${(n/1e3).toFixed(0)}k` : `$${Math.round(n)}`;
const fmt   = (n, d=1) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";
const capFst= (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g," ") : "—";

// ── Sub-components ────────────────────────────────────────────────────────
function StageBadge({ stage, small = false }) {
  const cfg = STAGES.find(s => s.key === stage) || STAGES[0];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.text} ${small?"text-xs":""}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.color}`} />
      {cfg.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${PRIORITY_STYLE[priority]||PRIORITY_STYLE.medium}`}>
      {capFst(priority)}
    </span>
  );
}

function ScoreBar({ score }) {
  const pct  = Math.min(100, Math.max(0, score||0));
  const color = pct>=68?"#059669":pct>=42?"#3B82F6":"#D97706";
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 bg-slate-100 rounded-full h-1.5 overflow-hidden">
        <div className="h-full rounded-full" style={{width:`${pct}%`,backgroundColor:color}} />
      </div>
      <span className="text-xs font-bold tabular-nums" style={{color}}>{fmt(score,0)}</span>
    </div>
  );
}

// ── Pipeline Stats Strip ──────────────────────────────────────────────────
function PipelineStrip({ stats, activeStage, onStageClick }) {
  return (
    <div className="grid grid-cols-3 lg:grid-cols-6 gap-2" data-testid="pipeline-strip">
      {STAGES.map(s => {
        const count = stats?.by_stage?.[s.key] || 0;
        const isActive = activeStage === s.key;
        return (
          <button key={s.key} onClick={() => onStageClick(isActive ? null : s.key)}
            data-testid={`stage-chip-${s.key}`}
            className={`rounded-xl p-3 text-center transition-all border-2 ${isActive
              ? `border-[#059669] bg-white shadow-sm`
              : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <div className={`w-2 h-2 rounded-full ${s.color} mx-auto mb-1`} />
            <p className="text-lg font-bold text-slate-900 tabular-nums">{count}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wide">{s.label}</p>
          </button>
        );
      })}
    </div>
  );
}

// ── Detail Panel ─────────────────────────────────────────────────────────
function DetailPanel({ deal, onClose, onUpdate }) {
  const [detail, setDetail]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [newNote, setNewNote]     = useState("");
  const [addingNote, setAddNote]  = useState(false);
  const [updatingStage, setUpdS]  = useState(false);
  const [updatingAssign, setUpdA] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    client.get(`/deals/${deal.pack_id}`)
      .then(r => { setDetail(r.data); setLoading(false); })
      .catch(() => { toast.error("Failed to load deal detail"); setLoading(false); });
  }, [deal.pack_id]);

  const handleStageChange = async (newStage) => {
    setUpdS(true);
    try {
      await client.patch(`/deals/${deal.pack_id}`, { pipeline_stage: newStage });
      toast.success(`Stage updated to "${capFst(newStage)}"`);
      const updated = await client.get(`/deals/${deal.pack_id}`);
      setDetail(updated.data);
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    setUpdS(false);
  };

  const handleAssignChange = async (userId) => {
    setUpdA(true);
    try {
      await client.patch(`/deals/${deal.pack_id}`, { assigned_to_user_id: userId ? Number(userId) : null });
      toast.success("Assignment updated");
      const updated = await client.get(`/deals/${deal.pack_id}`);
      setDetail(updated.data);
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    setUpdA(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddNote(true);
    try {
      await client.post(`/deals/${deal.pack_id}/notes`, { note: newNote.trim() });
      toast.success("Note added");
      setNewNote("");
      const updated = await client.get(`/deals/${deal.pack_id}`);
      setDetail(updated.data);
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add note"); }
    setAddNote(false);
  };

  const invest  = detail?.pack?.investment_case || {};
  const conf_l  = detail?.pack?.confidence_layer || {};
  const identity= detail?.pack?.identity || {};

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="detail-panel">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* Panel */}
      <div className="w-full lg:w-[520px] bg-white overflow-y-auto shadow-2xl">
        {/* Panel header */}
        <div className="sticky top-0 bg-[#0F172A] px-5 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider">Deal Detail</p>
            <p className="text-white font-bold text-sm">{deal.company}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(`/verify/pack/${deal.pack_id}`)} target="_blank"
              className="flex items-center gap-1 text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg hover:bg-slate-800">
              <ExternalLink size={11} /> View Pack
            </button>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800">
              <X size={15} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading...</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Identity */}
            <div className="grid grid-cols-2 gap-4">
              {[["Sector", identity.sector], ["Country", identity.country],
                ["Org Type", (identity.org_type||"sme").toUpperCase()], ["Reporting Year", identity.reporting_year ? `FY${identity.reporting_year}` : "—"]].map(([l,v]) => (
                <div key={l} className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 mb-0.5">{l}</p>
                  <p className="text-sm font-semibold text-slate-900">{String(v||"—")}</p>
                </div>
              ))}
            </div>

            {/* Deal score + priority */}
            <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl">
              <div className="flex-1">
                <p className="text-xs text-slate-400 mb-1">Deal Score</p>
                <ScoreBar score={detail?.deal?.deal_score} />
              </div>
              <PriorityBadge priority={detail?.deal?.priority} />
            </div>

            {/* Investment Case */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Investment Case</p>
              <div className="grid grid-cols-2 gap-2">
                {[["CapEx",fmtK(invest.total_capex_usd)],["Savings",`${fmtK(invest.total_annual_savings_usd)}/yr`],
                  ["CO₂/yr",`${fmt(invest.total_co2_reduction_tco2e_yr,1)}t`],["Payback",`${fmt(invest.weighted_payback_years,1)} yrs`]].map(([l,v]) => (
                  <div key={l} className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400">{l}</p>
                    <p className="text-sm font-semibold text-slate-900">{v}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Confidence */}
            <div className="p-3 border border-slate-200 rounded-xl">
              <div className="flex justify-between items-center">
                <p className="text-xs font-semibold text-slate-500">Data Confidence</p>
                <span className="text-sm font-semibold text-slate-900">{fmt(conf_l.overall_score,0)}/100</span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5 capitalize">{conf_l.tier_label || capFst(conf_l.tier)}</p>
            </div>

            {/* Stage Control */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Pipeline Stage</p>
              <div className="flex flex-wrap gap-1.5">
                {STAGES.map(s => (
                  <button key={s.key}
                    onClick={() => handleStageChange(s.key)}
                    disabled={updatingStage}
                    data-testid={`stage-btn-${s.key}`}
                    className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all
                      ${detail?.deal?.pipeline_stage === s.key
                        ? `border-[#059669] bg-emerald-50 text-emerald-700`
                        : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                    {detail?.deal?.pipeline_stage === s.key && <Check size={11} />}
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Assignment */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Assigned To</p>
              <select value={detail?.deal?.assigned_to_user_id || ""} disabled={updatingAssign}
                onChange={e => handleAssignChange(e.target.value || null)}
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#059669]">
                <option value="">Unassigned</option>
                {(detail?.available_users || []).map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>

            {/* FI Interests */}
            {(detail?.interests || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  FI Interest Expressions ({detail.interests.length})
                </p>
                <div className="space-y-2" data-testid="interests-list">
                  {detail.interests.filter(i => !i.name.startsWith("TEST_")).map(i => (
                    <div key={i.id} className="flex items-start justify-between p-3 bg-white border border-slate-200 rounded-xl">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{i.name}</p>
                        <p className="text-xs text-slate-400">{i.institution} · <a href={`mailto:${i.email}`} className="text-[#059669] hover:underline">{i.email}</a></p>
                        {i.notes && <p className="text-xs text-slate-400 mt-0.5 italic">"{i.notes}"</p>}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ml-2 ${INTEREST_STYLE[i.interest_level]||INTEREST_STYLE.medium}`}>
                        {capFst(i.interest_level)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Financing Review */}
            {detail?.review && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-xs font-semibold text-blue-700 mb-1">Financing Review</p>
                <p className="text-xs text-blue-600">{detail.review.review_id} · {capFst(detail.review.status)}</p>
              </div>
            )}

            {/* Activity Log */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Activity Log</p>
              {(detail?.notes || []).length === 0
                ? <p className="text-xs text-slate-400 italic">No notes yet.</p>
                : (
                  <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="activity-log">
                    {detail.notes.map(n => (
                      <div key={n.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="flex justify-between mb-1">
                          <span className="text-xs font-medium text-slate-600">{n.author}</span>
                          <span className="text-xs text-slate-400">{n.created_at?.slice(0,10)}</span>
                        </div>
                        <p className="text-xs text-slate-700 leading-relaxed">{n.note}</p>
                      </div>
                    ))}
                  </div>
                )
              }
              {/* Add note */}
              <div className="mt-3 flex gap-2">
                <textarea value={newNote} onChange={e => setNewNote(e.target.value)} rows={2}
                  placeholder="Add a note to the activity log..."
                  className="flex-1 text-xs border border-slate-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#059669]" />
                <button onClick={handleAddNote} disabled={addingNote || !newNote.trim()}
                  data-testid="add-note-btn"
                  className="px-3 py-2 bg-[#059669] text-white text-xs font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50 self-end">
                  {addingNote ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function DealsPage() {
  const [deals,    setDeals]    = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [stageFilter, setStageF]= useState(null);
  const [sortBy,   setSortBy]   = useState("deal_score");
  const [showFilters, setShowF] = useState(false);
  const [confFilter,  setConfF] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (stageFilter)       params.append("stage",          stageFilter);
      if (sortBy)            params.append("sort",           sortBy);
      if (confFilter)        params.append("min_confidence", confFilter);
      const [dRes, sRes] = await Promise.all([
        client.get(`/deals/?${params.toString()}`),
        client.get("/deals/stats"),
      ]);
      setDeals(dRes.data.deals || []);
      setStats(sRes.data);
    } catch { toast.error("Failed to load deals"); }
    setLoading(false);
  }, [stageFilter, sortBy, confFilter]);

  useEffect(() => { load(); }, [load]);

  const SORT_OPTIONS = [
    { key:"deal_score", label:"Deal Score" },
    { key:"capex",      label:"Highest CapEx" },
    { key:"payback",    label:"Fastest Payback" },
    { key:"savings",    label:"Most Savings" },
    { key:"confidence", label:"Highest Confidence" },
    { key:"interest",   label:"Highest Interest" },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6 min-h-screen" data-testid="deals-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <PageHeader title="Deal Pipeline" />

      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Active Deals" value={deals.length} color="text-slate-900" />
      </div>
          <p className="text-sm text-slate-500 mt-0.5">
            {stats?.total || 0} deals · {stats?.active_deals || 0} active · ${((stats?.total_capex_pipeline||0)/1000).toFixed(0)}k total pipeline value
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowF(!showFilters)} data-testid="filters-btn"
            className={`flex items-center gap-1.5 px-3 py-2 border text-xs font-medium rounded-xl transition-colors ${showFilters?"border-[#059669] bg-emerald-50 text-emerald-700":"border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
            <Filter size={12} /> Filters
          </button>
          <button onClick={load} disabled={loading} data-testid="refresh-btn"
            className="p-2 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-100">
            <RefreshCw size={13} className={loading?"animate-spin":""} />
          </button>
        </div>
      </div>

      {/* Pipeline strip */}
      {stats && <PipelineStrip stats={stats} activeStage={stageFilter} onStageClick={setStageF} />}

      {/* Filter bar */}
      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3 items-end" data-testid="filter-bar">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Minimum Confidence</label>
            <select value={confFilter} onChange={e => setConfF(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#059669]">
              <option value="">Any confidence</option>
              {[40,60,75].map(v => <option key={v} value={v}>≥{v}%</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Sort By</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#059669]">
              {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          {(stageFilter || confFilter) && (
            <button onClick={() => { setStageF(null); setConfF(""); }}
              className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1.5 border border-slate-200 rounded-lg">
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="deals-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {["Company","Deal Type","CapEx","Savings","Payback","Confidence","Interest","Verification","FRS","Stage","Score","Last Activity"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(10)].map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : deals.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center text-slate-400">
                    <TrendingUp size={28} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No deals match current filters</p>
                  </td>
                </tr>
              ) : deals.map(deal => (
                <tr key={deal.pack_id}
                  onClick={() => setSelected(deal)}
                  data-testid={`deal-row-${deal.pack_id}`}
                  className={`hover:bg-slate-50 cursor-pointer transition-colors ${selected?.pack_id === deal.pack_id ? "bg-emerald-50/30" : ""}`}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900 whitespace-nowrap">{deal.company}</p>
                    <p className="text-xs text-slate-400">{deal.country} · {deal.reporting_year ? `FY${deal.reporting_year}` : "—"}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{deal.deal_type}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900 tabular-nums whitespace-nowrap">{fmtK(deal.total_capex)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-[#059669] tabular-nums whitespace-nowrap">{fmtK(deal.annual_savings)}/yr</td>
                  <td className="px-4 py-3 text-sm font-semibold text-amber-600 tabular-nums whitespace-nowrap">{fmt(deal.payback_years,1)} yrs</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 bg-slate-100 rounded-full h-1 overflow-hidden">
                        <div className="h-full rounded-full bg-blue-500" style={{width:`${deal.confidence_score||0}%`}} />
                      </div>
                      <span className="text-xs tabular-nums text-slate-600">{fmt(deal.confidence_score,0)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {deal.interest_level_highest
                      ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${INTEREST_STYLE[deal.interest_level_highest]}`}>
                          {deal.interest_count} · {capFst(deal.interest_level_highest)}
                        </span>
                      : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {deal.verification_level ? (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        deal.verification_level==="third_party_verified"?"bg-emerald-50 text-emerald-700":
                        deal.verification_level==="analyst_reviewed"?"bg-indigo-50 text-indigo-700":
                        deal.verification_level==="system_validated"?"bg-blue-50 text-blue-700":
                        "bg-slate-100 text-slate-500"}`}>
                        {deal.verification_level==="third_party_verified"?"3P Verified":
                         deal.verification_level==="analyst_reviewed"?"Analyst":
                         deal.verification_level==="system_validated"?"System Val.":"Self-Reported"}
                      </span>
                    ) : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  {/* FRS column — inline badge per assessment */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {deal.assessment_id ? (
                      <FinancingReadinessScore
                        assessmentId={deal.assessment_id}
                        mode="inline"
                      />
                    ) : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap"><StageBadge stage={deal.pipeline_stage} small /></td>
                  <td className="px-4 py-3 whitespace-nowrap"><ScoreBar score={deal.deal_score} /></td>
                  <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{deal.last_activity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel */}
      {selected && (
        <DetailPanel
          deal={selected}
          onClose={() => setSelected(null)}
          onUpdate={load}
        />
      )}
    </div>
  );
}
